import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { riordinoApi } from '../services/api'
import { Navigation, X, Check, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react'
import toast from 'react-hot-toast'

interface OrdineItem {
  wo_id: number
  numero_progressivo: string
  denominazione?: string
  ordine_corrente?: number
  ordine_proposto: number
}

interface Richiesta {
  id: number
  nome_tecnico: string
  data_turno: string
  ordini_proposti: OrdineItem[]
  nota_tecnico?: string
  created_at: string
}

const ADMIN_ROLES = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico']

function fmtData(iso: string) {
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
function fmtOra(iso: string) {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

export function RichiestaRiordinoPopup() {
  const { user } = useAuth()
  const [richieste, setRichieste] = useState<Richiesta[]>([])
  const [expanded, setExpanded] = useState<number | null>(null)
  const [notaAdmin, setNotaAdmin] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState<Record<number, boolean>>({})

  const isAdmin = ADMIN_ROLES.includes(user?.role ?? '')

  useEffect(() => {
    if (!isAdmin) return
    const fetch = () => {
      riordinoApi.pendenti()
        .then(r => setRichieste(r.data))
        .catch(() => {})
    }
    fetch()
    const t = setInterval(fetch, 30_000)
    return () => clearInterval(t)
  }, [isAdmin])

  const handle = async (id: number, azione: 'approva' | 'rifiuta') => {
    setLoading(prev => ({ ...prev, [id]: true }))
    try {
      const nota = notaAdmin[id] || undefined
      if (azione === 'approva') {
        await riordinoApi.approva(id, nota)
        toast.success('Riordino approvato e applicato')
      } else {
        await riordinoApi.rifiuta(id, nota)
        toast.success('Richiesta rifiutata')
      }
      setRichieste(prev => prev.filter(r => r.id !== id))
      setExpanded(null)
    } catch {
      toast.error('Errore durante la gestione')
    } finally {
      setLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  if (!isAdmin || richieste.length === 0) return null

  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 24, zIndex: 9998,
      display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400,
    }}>
      {richieste.map(r => {
        const isExp = expanded === r.id
        const isLoad = loading[r.id]
        return (
          <div key={r.id} style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1.5px solid #bfdbfe',
            overflow: 'hidden',
          }}>
            {/* Header card */}
            <div
              style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: '#eff6ff' }}
              onClick={() => setExpanded(isExp ? null : r.id)}
            >
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1E40AF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Navigation size={16} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
                  Richiesta riordino giro
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                  {r.nome_tecnico} · {fmtData(r.data_turno)} · ore {fmtOra(r.created_at)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#1E40AF', color: '#fff' }}>
                  {r.ordini_proposti.length} WO
                </span>
                {isExp ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />}
              </div>
            </div>

            {/* Dettaglio espandibile */}
            {isExp && (
              <div style={{ padding: '12px 14px', borderTop: '1px solid #e0f2fe' }}>

                {/* Nota tecnico */}
                {r.nota_tecnico && (
                  <div style={{ padding: '6px 10px', background: '#fef9c3', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 10 }}>
                    💬 "{r.nota_tecnico}"
                  </div>
                )}

                {/* Tabella confronto ordini */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 6 }}>
                    Ordine proposto
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {[...r.ordini_proposti]
                      .sort((a, b) => a.ordine_proposto - b.ordine_proposto)
                      .map(item => {
                        const changed = item.ordine_corrente != null && item.ordine_corrente !== item.ordine_proposto
                        return (
                          <div key={item.wo_id} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '5px 8px', borderRadius: 8,
                            background: changed ? '#fef3c7' : '#f8fafc',
                            border: `1px solid ${changed ? '#fcd34d' : '#e2e8f0'}`,
                          }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: '#1E40AF', width: 20, textAlign: 'center', flexShrink: 0 }}>
                              #{item.ordine_proposto}
                            </span>
                            {changed && item.ordine_corrente != null && (
                              <>
                                <span style={{ fontSize: 10, color: '#9ca3af', textDecoration: 'line-through' }}>#{item.ordine_corrente}</span>
                                <ArrowRight size={10} color="#d97706" style={{ flexShrink: 0 }} />
                              </>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#1E40AF', fontWeight: 600 }}>
                                {item.numero_progressivo}
                              </div>
                              {item.denominazione && (
                                <div style={{ fontSize: 10, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {item.denominazione}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>

                {/* Nota admin */}
                <textarea
                  value={notaAdmin[r.id] ?? ''}
                  onChange={e => setNotaAdmin(prev => ({ ...prev, [r.id]: e.target.value }))}
                  placeholder="Nota (opzionale)..."
                  rows={2}
                  style={{
                    width: '100%', borderRadius: 8, border: '1px solid #e2e8f0',
                    padding: '6px 8px', fontSize: 12, resize: 'none',
                    fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box',
                  }}
                />

                {/* Azioni */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handle(r.id, 'approva')}
                    disabled={isLoad}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '10px 0', borderRadius: 10, background: '#16a34a', color: '#fff',
                      fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
                      opacity: isLoad ? 0.7 : 1,
                    }}
                  >
                    <Check size={14} /> Approva
                  </button>
                  <button
                    onClick={() => handle(r.id, 'rifiuta')}
                    disabled={isLoad}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '10px 0', borderRadius: 10, background: '#fff', color: '#dc2626',
                      fontWeight: 700, fontSize: 13, border: '1.5px solid #fca5a5', cursor: 'pointer',
                      opacity: isLoad ? 0.7 : 1,
                    }}
                  >
                    <X size={14} /> Rifiuta
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
